import { CONTACT_FIELDS, contactPreset, type ContactField, type ContactSettings } from "@hotel-surveys/shared";
import { type CSSProperties, FormEvent, useEffect, useState } from "react";
import { DndContext, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, horizontalListSortingStrategy, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Copy, GripVertical, Languages, ListOrdered, PauseCircle, Play, Plus, Smartphone, Trash2, X } from "lucide-react";
import { api, formatApiError, type Hotel, type LocationType, type LowScoreReasonGroup, type QrRoute, type Survey, type SurveyPreviewResolution } from "../api/client";
import { surveySectors, surveyStarterQuestions, type SurveySector } from "../features/survey-starters";

type SurveyQuestion = Survey["questions"][number];

const questionTypes = [
  "scale_1_5",
  "nps_0_10",
  "yes_no",
  "single_choice",
  "multiple_choice",
  "short_text",
  "long_text",
  "emoji_rating",
  "info_block",
  "matrix_rating"
] as const;

const supportedLanguages = ["en", "sr-Latn", "ru", "de", "fr", "es", "pt", "he"] as const;
const defaultLanguageOrder = ["en", "sr-Latn", "ru", "de", "fr", "es", "pt", "he"];
const languageLabels: Record<string, string> = {
  en: "Engleski",
  "sr-Latn": "Sr-Latin",
  ru: "Ruski",
  de: "Njemacki",
  fr: "Francuski",
  es: "Spanski",
  pt: "Portugalski",
  he: "Jevrejski"
};

export function SurveysPage() {
  const queryClient = useQueryClient();
  const [selectedSurveyId, setSelectedSurveyId] = useState("");
  const [starterSector, setStarterSector] = useState<SurveySector>("conference");
  const [previewQrId, setPreviewQrId] = useState("");
  const [previewAt, setPreviewAt] = useState("");
  const [previewLang, setPreviewLang] = useState("en");
  const [previewResult, setPreviewResult] = useState<SurveyPreviewResolution | null>(null);
  const [builderLanguages, setBuilderLanguages] = useState<string[]>(defaultLanguageOrder);
  const [builderLanguage, setBuilderLanguage] = useState("en");
  const [deleteQuestion, setDeleteQuestion] = useState<SurveyQuestion | null>(null);
  const [orderDialogSurveyId, setOrderDialogSurveyId] = useState("");
  const surveys = useQuery({ queryKey: ["surveys"], queryFn: () => api<Survey[]>("/api/admin/surveys") });
  const hotels = useQuery({ queryKey: ["hotels"], queryFn: () => api<Hotel[]>("/api/admin/hotels") });
  const qrRoutes = useQuery({ queryKey: ["qr-routes"], queryFn: () => api<QrRoute[]>("/api/admin/qr-routes") });
  const locationTypes = useQuery({ queryKey: ["location-types"], queryFn: () => api<LocationType[]>("/api/admin/location-types") });
  const lowScoreReasonGroups = useQuery({ queryKey: ["low-score-reason-groups"], queryFn: () => api<LowScoreReasonGroup[]>("/api/admin/low-score-reason-groups") });
  const activeSurvey = surveys.data?.find((survey) => survey.id === selectedSurveyId) ?? surveys.data?.[0];
  const orderDialogSurvey = surveys.data?.find((survey) => survey.id === orderDialogSurveyId) ?? null;
  const activeBuilderLanguages = normalizeBuilderLanguages(builderLanguages, activeSurvey?.defaultLanguage ?? "en");

  useEffect(() => {
    if (!activeSurvey) return;
    const next = surveyLanguageOrder(activeSurvey);
    setBuilderLanguages(next);
    setBuilderLanguage((current) => next.includes(current) ? current : next[0] ?? "en");
  }, [activeSurvey?.id, activeSurvey?.defaultLanguage, activeSurvey?.settings?.languageOrder]);

  const publish = useMutation({
    mutationFn: (id: string) => api(`/api/admin/surveys/${id}/publish`, { method: "POST", body: "{}" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["surveys"] })
  });

  const lifecycle = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "duplicate" | "pause" | "archive" }) => api<Survey>(`/api/admin/surveys/${id}/${action}`, { method: "POST", body: "{}" }),
    onSuccess: (survey, input) => {
      if (input.action === "duplicate") setSelectedSurveyId(survey.id);
      queryClient.invalidateQueries({ queryKey: ["surveys"] });
    }
  });

  const createSurvey = useMutation({
    mutationFn: (body: unknown) => api<Survey>("/api/admin/surveys", {
      method: "POST",
      body: JSON.stringify(body)
    }),
    onSuccess: (survey) => {
      setSelectedSurveyId(survey.id);
      queryClient.invalidateQueries({ queryKey: ["surveys"] });
    }
  });
  const updateSurvey = useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) => api<Survey>(`/api/admin/surveys/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["surveys"] })
  });

  const createQuestion = useMutation({
    mutationFn: (body: unknown) => api("/api/admin/questions", {
      method: "POST",
      body: JSON.stringify(body)
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["surveys"] })
  });
  const addStarter = useMutation({
    mutationFn: async () => {
      if (!activeSurvey || activeSurvey.questions.some(question => !isRemovedQuestion(question))) return;
      for (const question of surveyStarterQuestions(starterSector, activeSurvey.id, activeBuilderLanguages, activeSurvey.defaultLanguage)) {
        await api("/api/admin/questions", { method: "POST", body: JSON.stringify(question) });
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["surveys"] })
  });
  const updateQuestion = useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) => api(`/api/admin/questions/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body)
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["surveys"] })
  });
  const reorderQuestions = useMutation({
    mutationFn: (body: unknown) => api("/api/admin/questions/reorder", {
      method: "POST",
      body: JSON.stringify(body)
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["surveys"] })
  });
  const removeQuestion = useMutation({
    mutationFn: (id: string) => api(`/api/admin/questions/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setDeleteQuestion(null);
      queryClient.invalidateQueries({ queryKey: ["surveys"] });
    }
  });

  function submitSurvey(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const hotelId = String(form.get("hotelId") || "");
    const defaultLanguage = String(form.get("defaultLanguage") || "en");
    const languageOrder = normalizeBuilderLanguages([defaultLanguage, ...defaultLanguageOrder], defaultLanguage);
    createSurvey.mutate({
      hotelId: hotelId || undefined,
      name: String(form.get("name")),
      title: String(form.get("title")),
      description: String(form.get("description") || ""),
      defaultLanguage,
      settings: {
        roomPromptTiming: "before_submit",
        contact: contactSettingsFromForm(form),
        allowRoomCorrection: form.get("allowRoomCorrection") === "on",
        languageOrder
      }
    });
    event.currentTarget.reset();
  }

  function submitSurveySettings(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!activeSurvey) return;
    const form = new FormData(event.currentTarget);
    updateSurvey.mutate({
      id: activeSurvey.id,
      body: {
        defaultLanguage: activeBuilderLanguages[0] ?? activeSurvey.defaultLanguage,
        settings: {
          ...(activeSurvey.settings ?? {}),
          roomPromptTiming: "before_submit",
          contact: contactSettingsFromForm(form),
          allowRoomCorrection: form.get("allowRoomCorrection") === "on",
          languageOrder: activeBuilderLanguages
        }
      }
    });
  }

  function saveLanguageOrder(nextLanguages: string[]) {
    if (!activeSurvey) return;
    const normalized = normalizeBuilderLanguages(nextLanguages, nextLanguages[0] ?? activeSurvey.defaultLanguage);
    setBuilderLanguages(normalized);
    if (!normalized.includes(builderLanguage)) setBuilderLanguage(normalized[0] ?? "en");
    updateSurvey.mutate({
      id: activeSurvey.id,
      body: {
        defaultLanguage: normalized[0] ?? activeSurvey.defaultLanguage,
        settings: {
          ...(activeSurvey.settings ?? {}),
          languageOrder: normalized
        }
      }
    });
  }

  function toggleBuilderLanguage(language: string) {
    const next = activeBuilderLanguages.includes(language)
      ? activeBuilderLanguages.filter((item) => item !== language)
      : [...activeBuilderLanguages, language];
    saveLanguageOrder(next.length ? next : [language]);
  }

  function submitQuestion(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!activeSurvey) return;
    const form = new FormData(event.currentTarget);
    const builderQuestions = activeSurvey.questions.filter((question) => !isRemovedQuestion(question));
    createQuestion.mutate({
      surveyId: activeSurvey.id,
      internalKey: String(form.get("internalKey")),
      type: String(form.get("type")),
      analyticsCategory: String(form.get("analyticsCategory") || ""),
      required: form.get("required") === "on",
      criticalFlag: form.get("criticalFlag") === "on",
      alertThreshold: form.get("alertThreshold") ? Number(form.get("alertThreshold")) : undefined,
      enableLowScoreFollowup: form.get("enableLowScoreFollowup") === "on",
      lowScoreThreshold: form.get("lowScoreThreshold") ? Number(form.get("lowScoreThreshold")) : 2,
      lowScoreReasonGroupId: String(form.get("lowScoreReasonGroupId") || "") || null,
      lowScoreFollowupRequired: form.get("lowScoreFollowupRequired") === "on",
      displayOrder: builderQuestions.length + 10,
      label: firstTranslationLabel(form, activeBuilderLanguages),
      language: activeBuilderLanguages[0],
      translations: translationPayload(form, activeBuilderLanguages),
      config: {
        matrixRows: splitLines(String(form.get("matrixRows") || "")),
        visibility: {
          locationTypeCodes: form.get("visibleLocationType") ? [String(form.get("visibleLocationType"))] : [],
          mealPeriods: form.get("visibleMealPeriod") ? [String(form.get("visibleMealPeriod"))] : [],
          unitPattern: String(form.get("unitPattern") || ""),
          activeFrom: String(form.get("activeFrom") || ""),
          activeTo: String(form.get("activeTo") || ""),
          timeStart: String(form.get("timeStart") || ""),
          timeEnd: String(form.get("timeEnd") || "")
        }
      }
    });
    event.currentTarget.reset();
  }

  function moveQuestion(questionId: string, direction: -1 | 1) {
    if (!activeSurvey) return;
    const ordered = activeSurvey.questions
      .filter((question) => !isRemovedQuestion(question))
      .sort((a, b) => a.displayOrder - b.displayOrder);
    const index = ordered.findIndex((question) => question.id === questionId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= ordered.length) return;
    const current = ordered[index];
    const next = ordered[target];
    if (!current || !next) return;
    ordered[index] = next;
    ordered[target] = current;
    reorderQuestions.mutate({
      surveyId: activeSurvey.id,
      items: ordered.map((question, itemIndex) => ({ id: question.id, displayOrder: (itemIndex + 1) * 10 }))
    });
  }

  function handleQuestionDragEnd(event: DragEndEvent) {
    if (!activeSurvey || !event.over || event.active.id === event.over.id) return;
    const ordered = activeSurvey.questions
      .filter((question) => !isRemovedQuestion(question))
      .sort((a, b) => a.displayOrder - b.displayOrder);
    const oldIndex = ordered.findIndex((question) => question.id === event.active.id);
    const newIndex = ordered.findIndex((question) => question.id === event.over?.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const moved = arrayMove(ordered, oldIndex, newIndex);
    reorderQuestions.mutate({
      surveyId: activeSurvey.id,
      items: moved.map((question, itemIndex) => ({ id: question.id, displayOrder: (itemIndex + 1) * 10 }))
    });
  }

  function handleOrderDialogDragEnd(survey: Survey, event: DragEndEvent) {
    if (!event.over || event.active.id === event.over.id) return;
    const ordered = orderedQuestions(survey);
    const oldIndex = ordered.findIndex((question) => question.id === event.active.id);
    const newIndex = ordered.findIndex((question) => question.id === event.over?.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const moved = arrayMove(ordered, oldIndex, newIndex);
    reorderQuestions.mutate({
      surveyId: survey.id,
      items: moved.map((question, itemIndex) => ({ id: question.id, displayOrder: (itemIndex + 1) * 10 }))
    });
  }

  function handleLanguageDragEnd(event: DragEndEvent) {
    if (!event.over || event.active.id === event.over.id) return;
    const oldIndex = activeBuilderLanguages.findIndex((language) => language === event.active.id);
    const newIndex = activeBuilderLanguages.findIndex((language) => language === event.over?.id);
    if (oldIndex < 0 || newIndex < 0) return;
    saveLanguageOrder(arrayMove(activeBuilderLanguages, oldIndex, newIndex));
  }

  function submitQuestionEdit(event: FormEvent<HTMLFormElement>, question: SurveyQuestion) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    updateQuestion.mutate({
      id: question.id,
      body: {
        type: String(form.get("type")),
        analyticsCategory: String(form.get("analyticsCategory") || ""),
        required: form.get("required") === "on",
        criticalFlag: form.get("criticalFlag") === "on",
        alertThreshold: form.get("alertThreshold") ? Number(form.get("alertThreshold")) : null,
        enableLowScoreFollowup: form.get("enableLowScoreFollowup") === "on",
        lowScoreThreshold: form.get("lowScoreThreshold") ? Number(form.get("lowScoreThreshold")) : 2,
        lowScoreReasonGroupId: String(form.get("lowScoreReasonGroupId") || "") || null,
        lowScoreFollowupRequired: form.get("lowScoreFollowupRequired") === "on",
        active: form.get("active") === "on",
        config: {
          matrixRows: splitLines(String(form.get("matrixRows") || "")),
          visibility: {
            locationTypeCodes: form.get("visibleLocationType") ? [String(form.get("visibleLocationType"))] : [],
            mealPeriods: form.get("visibleMealPeriod") ? [String(form.get("visibleMealPeriod"))] : [],
            unitPattern: String(form.get("unitPattern") || ""),
            activeFrom: String(form.get("activeFrom") || ""),
            activeTo: String(form.get("activeTo") || ""),
            timeStart: String(form.get("timeStart") || ""),
            timeEnd: String(form.get("timeEnd") || "")
          }
        },
        translations: translationPayload(form, activeBuilderLanguages)
      }
    });
  }

  async function resolvePreview(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!previewQrId) return;
    const params = new URLSearchParams({ qrRouteId: previewQrId, lang: previewLang });
    if (previewAt) params.set("at", new Date(previewAt).toISOString());
    setPreviewResult(await api<SurveyPreviewResolution>(`/api/admin/preview/resolve-survey?${params.toString()}`));
  }

  const previewUrl = previewResult?.shortPath ? `${publicOrigin()}${previewResult.shortPath}?lang=${encodeURIComponent(previewLang)}` : "";
  const builderQuestions = activeSurvey?.questions
    .filter((question) => !isRemovedQuestion(question))
    .sort((a, b) => a.displayOrder - b.displayOrder) ?? [];
  const mutationError = publish.error
    ?? lifecycle.error
    ?? createSurvey.error
    ?? updateSurvey.error
    ?? createQuestion.error
    ?? updateQuestion.error
    ?? reorderQuestions.error
    ?? removeQuestion.error;

  return (
    <div className="page-stack">
      <section className="tool-hero card survey-hero">
        <div>
          <span className="panel-eyebrow left">Survey Builder</span>
          <h2>Pitanja, prevodi i verzije po objektu.</h2>
          <p>Menadzeri i IT/marketing timovi odrzavaju samo svoje ankete i svoje objekte.</p>
          {mutationError && <p className="form-error">{formatApiError(mutationError)}</p>}
        </div>
        <div className="survey-phone-preview">
          <Smartphone size={22} />
          <strong>{activeSurvey?.name ?? "Default Guest Pulse"}</strong>
          <span>1. Koliko ste zadovoljni uslugom?</span>
          <div className="mini-rating">{[1, 2, 3, 4, 5].map((value) => <b key={value}>{value}</b>)}</div>
        </div>
      </section>

      <section className="panel card">
        <div className="panel-header">
          <div>
            <h2>Nova anketa</h2>
            <p>Kreiraj anketu globalno ili za konkretan scoped objekat.</p>
          </div>
        </div>
        <form className="inline-form survey-create-form" onSubmit={submitSurvey}>
          <select name="hotelId">
            <option value="">Organizacija fallback</option>
            {(hotels.data ?? []).map((hotel) => <option key={hotel.id} value={hotel.id}>{hotel.name}</option>)}
          </select>
          <input name="name" placeholder="Interni naziv ankete" required />
          <input name="title" placeholder="Naslov za gosta" required />
          <input name="description" placeholder="Opis, opciono" />
          <select name="defaultLanguage" defaultValue="en">
            {supportedLanguages.map((language) => (
              <option key={language} value={language}>{languageLabels[language]} ({language})</option>
            ))}
          </select>
          <ContactSettingsEditor />
          <label className="check-control"><input name="allowRoomCorrection" type="checkbox" defaultChecked /> Dozvoli ispravku sobe iz QR-a</label>
          <button type="submit"><Plus size={16} /> Kreiraj</button>
        </form>
      </section>

      {activeSurvey && (
        <section className="panel card">
          <div className="panel-header">
            <div>
              <h2>Zavrsnica ankete</h2>
              <p>Kontakt se nudi tek poslije svih pitanja i komentara. Podaci se traže samo kada gost izabere Da.</p>
            </div>
          </div>
          <form className="inline-form" onSubmit={submitSurveySettings}>
            <ContactSettingsEditor key={activeSurvey.id + JSON.stringify(activeSurvey.settings?.contact)} initial={activeSurvey.settings?.contact} />
            <label className="check-control"><input name="allowRoomCorrection" type="checkbox" defaultChecked={activeSurvey.settings?.allowRoomCorrection !== false} /> Dozvoli ispravku sobe iz QR-a</label>
            <button type="submit"><Plus size={16} /> Sacuvaj zavrsnicu</button>
          </form>
          {!activeSurvey.questions.some(question => !isRemovedQuestion(question)) && <div className="inline-form">
            <select aria-label="Namjena početnih pitanja" value={starterSector} onChange={event => setStarterSector(event.target.value as SurveySector)}>
              {Object.entries(surveySectors).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
            <button type="button" disabled={addStarter.isPending} onClick={() => addStarter.mutate()}>Dodaj početna pitanja</button>
            <p className="form-note">Tri početna pitanja možete urediti prije objave. Alarm i obavezna pitanja uključujete prema svojim potrebama.</p>
            {addStarter.isError && <p role="alert">{formatApiError(addStarter.error)}</p>}
          </div>}
        </section>
      )}

      <section className="panel card">
        <div className="panel-header">
          <div>
            <h2>Preview kao gost</h2>
            <p>Provjeri koju published verziju vidi konkretan QR, u konkretnom vremenu i jeziku.</p>
          </div>
        </div>
        <form className="inline-form" onSubmit={resolvePreview}>
          <select value={previewQrId} onChange={(event) => setPreviewQrId(event.target.value)} required>
            <option value="">Izaberi QR rutu</option>
            {(qrRoutes.data ?? []).map((route) => (
              <option key={route.id} value={route.id}>{route.shortPath} - {route.label}</option>
            ))}
          </select>
          <input type="datetime-local" value={previewAt} onChange={(event) => setPreviewAt(event.target.value)} />
          <select value={previewLang} onChange={(event) => setPreviewLang(event.target.value)}>
            {supportedLanguages.map((language) => (
              <option key={language} value={language}>{language}</option>
            ))}
          </select>
          <button type="submit"><Smartphone size={16} /> Resolve preview</button>
        </form>
        {previewResult && (
          <div className="preview-result survey-preview-result">
            <div>
              <strong>{previewResult.surveyName ?? "Nema published ankete"}</strong>
              <span>Ruta: {previewResult.shortPath}</span>
              <span>Meal period: {previewResult.mealPeriod}</span>
              <span>Rule: {previewResult.matchedRuleId ?? "fallback"}</span>
              <span>Version: {previewResult.versionNumber ?? "-"}</span>
            </div>
            {previewUrl && <a href={previewUrl} target="_blank" rel="noreferrer">Otvori guest preview</a>}
          </div>
        )}
      </section>

      <section className="panel card">
        <div className="panel-header">
          <div>
            <h2>Ankete</h2>
            <p>Draft/published verzije, stabilni internal_key i translation status po jeziku.</p>
          </div>
          {activeSurvey && (
            <div className="toolbar">
              <button onClick={() => publish.mutate(activeSurvey.id)}><Play size={16} /> Publish</button>
              <button onClick={() => lifecycle.mutate({ id: activeSurvey.id, action: "duplicate" })}><Copy size={16} /> Duplicate</button>
              <button onClick={() => lifecycle.mutate({ id: activeSurvey.id, action: "pause" })}><PauseCircle size={16} /> Pause</button>
              <button onClick={() => lifecycle.mutate({ id: activeSurvey.id, action: "archive" })}><Archive size={16} /> Archive</button>
            </div>
          )}
        </div>
        <div className="survey-list">
          {(surveys.data ?? []).map((survey) => (
            <article className={activeSurvey?.id === survey.id ? "survey-row selected" : "survey-row"} key={survey.id} onClick={() => setSelectedSurveyId(survey.id)}>
              <div>
                <strong>{survey.name}</strong>
                <span>{survey.status} - default {survey.defaultLanguage}</span>
              </div>
              <div className="translation-pills">
                {supportedLanguages.map((language) => (
                  <span key={language} className={survey.translations.some((item) => item.language === language) ? "complete" : "missing"}>
                    <Languages size={13} /> {language}
                  </span>
                ))}
              </div>
              <div className="row-actions">
                <button onClick={(event) => { event.stopPropagation(); setOrderDialogSurveyId(survey.id); }}><ListOrdered size={16} /> Redosljed</button>
                <button onClick={(event) => { event.stopPropagation(); publish.mutate(survey.id); }}><Play size={16} /> Publish</button>
                <button onClick={(event) => { event.stopPropagation(); lifecycle.mutate({ id: survey.id, action: "duplicate" }); }}><Copy size={16} /> Copy</button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="panel card">
        <div className="panel-header">
          <div>
            <h2>Pitanja</h2>
            <p>Dodaj pitanje u izabranu anketu; stable key ostaje osnova analitike.</p>
          </div>
        </div>
        <div className="builder-toolbar">
          <div>
            <strong>Jezici ankete</strong>
            <span>Prvi jezik je osnovni. Prevuci jezike lijevo/desno da odredis redosljed prikaza i tabova.</span>
          </div>
          <LanguageOrderControl
            languages={activeBuilderLanguages}
            activeLanguage={builderLanguage}
            onSelect={setBuilderLanguage}
            onDragEnd={handleLanguageDragEnd}
            selectedLanguages={activeBuilderLanguages}
            onToggle={toggleBuilderLanguage}
          />
        </div>
        <LanguageTabs languages={activeBuilderLanguages} activeLanguage={builderLanguage} onSelect={setBuilderLanguage} />
        <form className="inline-form question-create-form" onSubmit={submitQuestion}>
          <input name="internalKey" placeholder="internal_key" required pattern="[a-z0-9_]+" />
          <select name="type" defaultValue="scale_1_5">
            {questionTypes.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
          <input name="analyticsCategory" placeholder="category" />
          <input name="alertThreshold" type="number" min="0" max="10" placeholder="alert <= n" />
          <input name="lowScoreThreshold" type="number" min="0" max="10" defaultValue={2} placeholder="low-score <= n" />
          <ReasonGroupSelect groups={lowScoreReasonGroups.data ?? []} />
          <TranslationPanels languages={activeBuilderLanguages} activeLanguage={builderLanguage} />
          <OptionRows languages={activeBuilderLanguages} activeLanguage={builderLanguage} />
          <textarea name="matrixRows" rows={3} placeholder={"Matrix redovi:\nCistoca\nUsluga\nHrana"} />
          <select name="visibleLocationType">
            <option value="">Vidljivo na svim lokacijama</option>
            {(locationTypes.data ?? []).map((type) => <option key={type.id} value={type.code}>{type.name}</option>)}
          </select>
          <select name="visibleMealPeriod">
            <option value="">Bilo koji meal period</option>
            <option value="breakfast">Breakfast</option>
            <option value="lunch">Lunch</option>
            <option value="dinner">Dinner</option>
            <option value="outside_window">Outside window</option>
          </select>
          <input name="unitPattern" placeholder="Unit regex, npr. ^3[0-9]{2}$" />
          <input name="activeFrom" type="date" aria-label="Pitanje aktivno od" />
          <input name="activeTo" type="date" aria-label="Pitanje aktivno do" />
          <input name="timeStart" type="time" aria-label="Dnevni prozor od" />
          <input name="timeEnd" type="time" aria-label="Dnevni prozor do" />
          <label className="check-control"><input name="required" type="checkbox" /> Required</label>
          <label className="check-control"><input name="criticalFlag" type="checkbox" /> Critical</label>
          <label className="check-control"><input name="enableLowScoreFollowup" type="checkbox" /> Low-score follow-up</label>
          <label className="check-control"><input name="lowScoreFollowupRequired" type="checkbox" /> Follow-up required</label>
          <button type="submit" disabled={!activeSurvey}><Plus size={16} /> Dodaj pitanje</button>
        </form>
        {activeSurvey && <SurveyVisualPreview survey={activeSurvey} language={builderLanguage} />}
        {activeSurvey && (
          <DndContext onDragEnd={handleQuestionDragEnd}>
            <SortableContext items={builderQuestions.map((question) => question.id)} strategy={verticalListSortingStrategy}>
              <div className="question-stack">
                {builderQuestions.map((question) => (
                  <SortableQuestionRow
                    key={question.id}
                    question={question}
                    locationTypes={locationTypes.data ?? []}
                    languages={activeBuilderLanguages}
                    activeLanguage={builderLanguage}
                    lowScoreReasonGroups={lowScoreReasonGroups.data ?? []}
                    onSelectLanguage={setBuilderLanguage}
                    onMove={moveQuestion}
                    onPauseToggle={(item) => updateQuestion.mutate({ id: item.id, body: { active: item.active === false } })}
                    onRemove={(item) => setDeleteQuestion(item)}
                    onSubmitEdit={submitQuestionEdit}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </section>
      {deleteQuestion && (
        <div className="modal-backdrop" role="presentation">
          <div className="confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="remove-question-title">
            <h2 id="remove-question-title">Izbaciti pitanje?</h2>
            <p>Pitanje <strong>{deleteQuestion.internalKey}</strong> se ne brise iz historije odgovora. Bice deaktivirano i anketa prelazi u draft dok ponovo ne publishujes verziju.</p>
            <div className="toolbar">
              <button type="button" onClick={() => setDeleteQuestion(null)}>Odustani</button>
              <button type="button" onClick={() => removeQuestion.mutate(deleteQuestion.id)}><Trash2 size={16} /> Izbaci pitanje</button>
            </div>
          </div>
        </div>
      )}
      {orderDialogSurvey && (
        <div className="modal-backdrop" role="presentation">
          <div className="question-order-dialog" role="dialog" aria-modal="true" aria-labelledby="question-order-title">
            <div className="dialog-header">
              <div>
                <h2 id="question-order-title">Redosljed pitanja</h2>
                <p>{orderDialogSurvey.name} - prevuci pitanje na mjesto gdje treba da se prikaze gostu.</p>
              </div>
              <button type="button" className="icon-button" onClick={() => setOrderDialogSurveyId("")} aria-label="Zatvori popup">
                <X size={18} />
              </button>
            </div>
            <DndContext onDragEnd={(event) => handleOrderDialogDragEnd(orderDialogSurvey, event)}>
              <SortableContext items={orderedQuestions(orderDialogSurvey).map((question) => question.id)} strategy={verticalListSortingStrategy}>
                <div className="question-order-list">
                  {orderedQuestions(orderDialogSurvey).map((question, index) => (
                    <SortableQuestionOrderItem key={question.id} question={question} index={index} language={builderLanguage} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            {orderedQuestions(orderDialogSurvey).length === 0 && (
              <div className="empty-state">Ova anketa jos nema pitanja. Dodaj pitanja u builderu, pa ih ovdje poredjaj.</div>
            )}
            <div className="dialog-footer">
              <span>{reorderQuestions.isPending ? "Cuvam redosljed..." : "Promjena redosljeda automatski vraca anketu u draft dok je ponovo ne publishujes."}</span>
              <button type="button" onClick={() => setOrderDialogSurveyId("")}>Gotovo</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SortableQuestionOrderItem({ question, index, language }: { question: SurveyQuestion; index: number; language: string }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: question.id });
  const translation = questionTranslation(question, language);
  const style: CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition
  };

  return (
    <div className="question-order-item" ref={setNodeRef} style={style}>
      <button className="icon-button drag-handle" type="button" {...attributes} {...listeners} aria-label={`Pomjeri pitanje ${index + 1}`}>
        <GripVertical size={17} />
      </button>
      <span className="question-order-number">{index + 1}</span>
      <div>
        <strong>{translation?.label ?? question.internalKey}</strong>
        <span>{question.internalKey} - {question.type}{question.required ? " - required" : ""}</span>
      </div>
      {question.active === false && <small>Paused</small>}
    </div>
  );
}

function LanguageOrderControl({
  languages,
  activeLanguage,
  selectedLanguages,
  onSelect,
  onDragEnd,
  onToggle
}: {
  languages: string[];
  activeLanguage: string;
  selectedLanguages: string[];
  onSelect: (language: string) => void;
  onDragEnd: (event: DragEndEvent) => void;
  onToggle: (language: string) => void;
}) {
  return (
    <div className="language-order-control">
      <DndContext onDragEnd={onDragEnd}>
        <SortableContext items={languages} strategy={horizontalListSortingStrategy}>
          <div className="language-order-strip" aria-label="Redosljed jezika ankete">
            {languages.map((language, index) => (
              <SortableLanguageChip
                key={language}
                language={language}
                isPrimary={index === 0}
                isActive={language === activeLanguage}
                onSelect={onSelect}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <div className="language-picker compact">
        {supportedLanguages.map((language) => {
          const selected = selectedLanguages.includes(language);
          return (
            <button
              key={language}
              type="button"
              className={selected ? "selected" : ""}
              onClick={() => onToggle(language)}
            >
              {selected ? "Ukloni" : "Dodaj"} {languageLabels[language]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SortableLanguageChip({
  language,
  isPrimary,
  isActive,
  onSelect
}: {
  language: string;
  isPrimary: boolean;
  isActive: boolean;
  onSelect: (language: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: language });
  const style: CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition
  };

  return (
    <div className={isActive ? "language-order-chip active" : "language-order-chip"} ref={setNodeRef} style={style}>
      <button className="icon-button drag-handle" type="button" {...attributes} {...listeners} aria-label={`Pomjeri ${languageLabels[language] ?? language}`}>
        <GripVertical size={15} />
      </button>
      <button type="button" className="language-chip-main" onClick={() => onSelect(language)}>
        <strong>{languageLabels[language] ?? language}</strong>
        <span>{language}</span>
      </button>
      {isPrimary && <small>Osnovni</small>}
    </div>
  );
}

function SortableQuestionRow({
  question,
  locationTypes,
  languages,
  activeLanguage,
  lowScoreReasonGroups,
  onSelectLanguage,
  onMove,
  onPauseToggle,
  onRemove,
  onSubmitEdit
}: {
  question: SurveyQuestion;
  locationTypes: LocationType[];
  languages: string[];
  activeLanguage: string;
  lowScoreReasonGroups: LowScoreReasonGroup[];
  onSelectLanguage: (language: string) => void;
  onMove: (questionId: string, direction: -1 | 1) => void;
  onPauseToggle: (question: SurveyQuestion) => void;
  onRemove: (question: SurveyQuestion) => void;
  onSubmitEdit: (event: FormEvent<HTMLFormElement>, question: SurveyQuestion) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: question.id });
  const visibility = visibilityConfig(question);
  const style: CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition
  };

  return (
    <details className="question-row" ref={setNodeRef} style={style}>
      <summary>
        <button className="icon-button" type="button" {...attributes} {...listeners} aria-label="Drag question">
          <GripVertical size={16} />
        </button>
        <code>{question.internalKey}</code>
        <span>{question.type}</span>
        <small>{question.active === false ? "paused" : `order ${question.displayOrder}`}</small>
        <div className="row-actions">
          <button type="button" onClick={(event) => { event.preventDefault(); onMove(question.id, -1); }}>Up</button>
          <button type="button" onClick={(event) => { event.preventDefault(); onMove(question.id, 1); }}>Down</button>
          <button type="button" onClick={(event) => { event.preventDefault(); onPauseToggle(question); }}>
            {question.active === false ? "Activate" : "Pause"}
          </button>
          <button type="button" onClick={(event) => { event.preventDefault(); onRemove(question); }}><Trash2 size={14} /> Izbaci</button>
        </div>
      </summary>
      <form className="inline-form question-edit-form" onSubmit={(event) => onSubmitEdit(event, question)}>
        <select name="type" defaultValue={question.type}>
          {questionTypes.map((type) => <option key={type} value={type}>{type}</option>)}
        </select>
        <input name="analyticsCategory" defaultValue={question.analyticsCategory ?? ""} placeholder="category" />
        <input name="alertThreshold" type="number" min="0" max="10" defaultValue={question.alertThreshold ?? ""} placeholder="alert <= n" />
        <input name="lowScoreThreshold" type="number" min="0" max="10" defaultValue={question.lowScoreThreshold ?? 2} placeholder="low-score <= n" />
        <ReasonGroupSelect groups={lowScoreReasonGroups} selectedId={question.lowScoreReasonGroupId ?? ""} />
        <LanguageTabs languages={languages} activeLanguage={activeLanguage} onSelect={onSelectLanguage} />
        <TranslationPanels languages={languages} activeLanguage={activeLanguage} question={question} />
        <OptionRows languages={languages} activeLanguage={activeLanguage} question={question} />
        <textarea name="matrixRows" rows={3} defaultValue={matrixText(question.config)} placeholder={"Matrix redovi:\nCistoca\nUsluga\nHrana"} />
        <select name="visibleLocationType" defaultValue={visibility.locationTypeCode}>
          <option value="">Vidljivo na svim lokacijama</option>
          {locationTypes.map((type) => <option key={type.id} value={type.code}>{type.name}</option>)}
        </select>
        <select name="visibleMealPeriod" defaultValue={visibility.mealPeriod}>
          <option value="">Bilo koji meal period</option>
          <option value="breakfast">Breakfast</option>
          <option value="lunch">Lunch</option>
          <option value="dinner">Dinner</option>
          <option value="outside_window">Outside window</option>
        </select>
        <input name="unitPattern" defaultValue={visibility.unitPattern} placeholder="Unit regex, npr. ^3[0-9]{2}$" />
        <input name="activeFrom" type="date" defaultValue={visibility.activeFrom} aria-label="Pitanje aktivno od" />
        <input name="activeTo" type="date" defaultValue={visibility.activeTo} aria-label="Pitanje aktivno do" />
        <input name="timeStart" type="time" defaultValue={visibility.timeStart} aria-label="Dnevni prozor od" />
        <input name="timeEnd" type="time" defaultValue={visibility.timeEnd} aria-label="Dnevni prozor do" />
        <label className="check-control"><input name="required" type="checkbox" defaultChecked={question.required === true} /> Required</label>
        <label className="check-control"><input name="criticalFlag" type="checkbox" defaultChecked={question.criticalFlag === true} /> Critical</label>
        <label className="check-control"><input name="enableLowScoreFollowup" type="checkbox" defaultChecked={question.enableLowScoreFollowup === true} /> Low-score follow-up</label>
        <label className="check-control"><input name="lowScoreFollowupRequired" type="checkbox" defaultChecked={question.lowScoreFollowupRequired === true} /> Follow-up required</label>
        <label className="check-control"><input name="active" type="checkbox" defaultChecked={question.active !== false} /> Active</label>
        <button type="submit"><Plus size={16} /> Sacuvaj pitanje</button>
      </form>
    </details>
  );
}

function LanguageTabs({ languages, activeLanguage, onSelect }: { languages: string[]; activeLanguage: string; onSelect: (language: string) => void }) {
  return (
    <div className="language-tabs" role="tablist" aria-label="Jezici">
      {languages.map((language) => (
        <button
          key={language}
          type="button"
          className={language === activeLanguage ? "selected" : ""}
          onClick={() => onSelect(language)}
        >
          {language}
        </button>
      ))}
    </div>
  );
}

function ReasonGroupSelect({ groups, selectedId = "" }: { groups: LowScoreReasonGroup[]; selectedId?: string }) {
  return (
    <select name="lowScoreReasonGroupId" defaultValue={selectedId}>
      <option value="">Default/general reason grupa</option>
      {groups.filter((group) => group.active).map((group) => (
        <option key={group.id} value={group.id}>
          {group.name} ({group.category}) - {group.reasons.filter((reason) => reason.active).length} razloga
        </option>
      ))}
    </select>
  );
}

function TranslationPanels({ languages, activeLanguage, question }: { languages: string[]; activeLanguage: string; question?: SurveyQuestion }) {
  return (
    <div className="translation-editor">
      {languages.map((language) => {
        const translation = question ? questionTranslation(question, language) : undefined;
        return (
          <div key={language} className="translation-panel" hidden={language !== activeLanguage}>
            <strong>{language} tekst pitanja</strong>
            <input name={`label:${language}`} defaultValue={translation?.label ?? ""} placeholder={`Label pitanja (${language})`} />
            <input name={`helperText:${language}`} defaultValue={translation?.helperText ?? ""} placeholder={`Helper tekst (${language})`} />
            <input name={`placeholder:${language}`} defaultValue={translation?.placeholder ?? ""} placeholder={`Placeholder (${language})`} />
          </div>
        );
      })}
    </div>
  );
}

function OptionRows({ languages, activeLanguage, question }: { languages: string[]; activeLanguage: string; question?: SurveyQuestion }) {
  const rows = optionRows(question);
  return (
    <div className="option-editor">
      <div className="option-editor-header">
        <strong>Opcije odgovora</strong>
        <span>Koristi se za single/multiple choice. `value` ostaje stabilan za analitiku.</span>
      </div>
      {rows.map((row, index) => (
        <div className="option-row" key={index}>
          <input name="optionValue" defaultValue={row.value} placeholder="value, npr. too_slow" />
          {languages.map((language) => (
            <input
              key={language}
              name={`optionLabel:${language}`}
              defaultValue={row.labels[language] ?? ""}
              placeholder={`Label (${language})`}
              hidden={language !== activeLanguage}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function SurveyVisualPreview({ survey, language }: { survey: Survey; language: string }) {
  const questions = orderedQuestions(survey).filter((question) => question.active !== false);
  return (
    <div className="builder-preview">
      <div>
        <strong>Vizuelni preview</strong>
        <span>{survey.name} - {language}</span>
      </div>
      <div className="builder-preview-phone">
        {questions.length === 0 && <p>Dodaj prvo pitanje da vidis tok ankete.</p>}
        {questions.map((question, index) => {
          const translation = questionTranslation(question, language);
          return (
            <section key={question.id}>
              <small>{index + 1}. {question.type}</small>
              <strong>{translation?.label ?? question.internalKey}</strong>
              {translation?.helperText && <span>{translation.helperText}</span>}
              {previewControl(question, translation?.options)}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function orderedQuestions(survey: Survey) {
  return [...survey.questions]
    .filter((question) => !isRemovedQuestion(question))
    .sort((a, b) => a.displayOrder - b.displayOrder);
}

function previewControl(question: SurveyQuestion, optionsValue: unknown) {
  if (question.type === "scale_1_5" || question.type === "emoji_rating") {
    return <div className="preview-rating">{[1, 2, 3, 4, 5].map((value) => <b key={value}>{value}</b>)}</div>;
  }
  if (question.type === "nps_0_10") {
    return <div className="preview-rating nps">{[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => <b key={value}>{value}</b>)}</div>;
  }
  if (question.type === "yes_no") {
    return <div className="preview-options"><span>Da</span><span>Ne</span></div>;
  }
  if (question.type === "single_choice" || question.type === "multiple_choice") {
    const options = parsedOptions(optionsValue);
    return <div className="preview-options">{(options.length ? options : [{ value: "option", label: "Opcija" }]).map((option) => <span key={option.value}>{option.label}</span>)}</div>;
  }
  if (question.type === "matrix_rating") {
    return <div className="preview-input">Matrix rating 1-5</div>;
  }
  if (question.type === "info_block") {
    return <div className="preview-input">Info blok</div>;
  }
  return <div className="preview-input">Tekstualni odgovor</div>;
}

function splitLines(value: string) {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function questionTranslation(question: SurveyQuestion, language: string) {
  return question.translations.find((item) => item.language === language)
    ?? question.translations.find((item) => item.language === "sr-Latn")
    ?? question.translations[0];
}

function translationPayload(form: FormData, languages: string[]) {
  const values = form.getAll("optionValue").map((value) => String(value).trim());
  return languages.map((language) => {
    const options = values.flatMap((value, index) => {
      if (!value) return [];
      const labels = form.getAll(`optionLabel:${language}`).map((label) => String(label).trim());
      return [{ value, label: labels[index] || value }];
    });
    return {
      language,
      label: String(form.get(`label:${language}`) || "").trim() || firstTranslationLabel(form, languages),
      helperText: String(form.get(`helperText:${language}`) || "").trim(),
      placeholder: String(form.get(`placeholder:${language}`) || "").trim(),
      options: { options }
    };
  });
}

function firstTranslationLabel(form: FormData, languages: string[]) {
  for (const language of languages) {
    const label = String(form.get(`label:${language}`) || "").trim();
    if (label) return label;
  }
  return "Novo pitanje";
}

function optionRows(question?: SurveyQuestion) {
  const byValue = new Map<string, { value: string; labels: Record<string, string> }>();
  for (const translation of question?.translations ?? []) {
    for (const option of parsedOptions(translation.options)) {
      const row = byValue.get(option.value) ?? { value: option.value, labels: {} };
      row.labels[translation.language] = option.label;
      byValue.set(option.value, row);
    }
  }
  const rows = [...byValue.values()];
  while (rows.length < 4) rows.push({ value: "", labels: {} });
  return rows;
}

function parsedOptions(optionsValue: unknown): Array<{ value: string; label: string }> {
  const options = optionsValue && typeof optionsValue === "object" && !Array.isArray(optionsValue)
    ? (optionsValue as { options?: unknown }).options
    : optionsValue;
  if (!Array.isArray(options)) return [];
  return options.flatMap((option) => {
    if (!option || typeof option !== "object") return [];
    const record = option as { value?: unknown; label?: unknown };
    return typeof record.value === "string" ? [{ value: record.value, label: typeof record.label === "string" ? record.label : record.value }] : [];
  });
}

function surveyLanguageOrder(survey?: Survey | null) {
  const settingsOrder = Array.isArray(survey?.settings?.languageOrder)
    ? survey.settings.languageOrder
    : undefined;
  return normalizeBuilderLanguages(settingsOrder?.length ? settingsOrder : [survey?.defaultLanguage ?? "en", ...defaultLanguageOrder], survey?.defaultLanguage ?? "en");
}

function normalizeBuilderLanguages(languages: string[], fallback: string) {
  const normalizedFallback = supportedLanguages.includes(fallback as typeof supportedLanguages[number]) ? fallback : "en";
  const ordered = [...languages, normalizedFallback]
    .filter((language, index, items) => supportedLanguages.includes(language as typeof supportedLanguages[number]) && items.indexOf(language) === index);
  if (ordered.length) return ordered;
  return [...defaultLanguageOrder];
}

function isRemovedQuestion(question: SurveyQuestion) {
  const config = question.config && typeof question.config === "object" && !Array.isArray(question.config)
    ? question.config as { removedFromBuilder?: unknown }
    : {};
  return config.removedFromBuilder === true;
}

function matrixText(config: unknown) {
  const rows = config && typeof config === "object" ? (config as { matrixRows?: unknown }).matrixRows : undefined;
  return Array.isArray(rows) ? rows.filter((item): item is string => typeof item === "string").join("\n") : "";
}

function visibilityConfig(question: SurveyQuestion) {
  const config = question.config && typeof question.config === "object" ? question.config as { visibility?: Record<string, unknown> } : {};
  const visibility = config.visibility ?? {};
  return {
    locationTypeCode: firstString(visibility.locationTypeCodes),
    mealPeriod: firstString(visibility.mealPeriods),
    unitPattern: typeof visibility.unitPattern === "string" ? visibility.unitPattern : "",
    activeFrom: typeof visibility.activeFrom === "string" ? visibility.activeFrom : "",
    activeTo: typeof visibility.activeTo === "string" ? visibility.activeTo : "",
    timeStart: typeof visibility.timeStart === "string" ? visibility.timeStart : "",
    timeEnd: typeof visibility.timeEnd === "string" ? visibility.timeEnd : ""
  };
}

function firstString(value: unknown) {
  return Array.isArray(value) && typeof value[0] === "string" ? value[0] : "";
}

function publicOrigin() {
  if (window.location.port === "5173" || window.location.port === "5176") {
    return `${window.location.protocol}//${window.location.hostname}:3001`;
  }
  return window.location.origin;
}

const contactFieldLabels: Record<ContactField, string> = { fullName: "Ime i prezime", roomNumber: "Broj sobe", email: "Email", phone: "Telefon" };

function contactSettingsFromForm(form: FormData): ContactSettings {
  const profile = String(form.get("contactProfile") || "automatic") as ContactSettings["profile"];
  return {
    enabled: form.get("contactEnabled") === "on", profile,
    recipient: String(form.get("contactRecipient") || "team") as ContactSettings["recipient"],
    fields: profile === "automatic" ? undefined : CONTACT_FIELDS.filter(key => form.get(`contactField:${key}`) === "on")
      .map(key => ({ key, required: form.get(`contactRequired:${key}`) === "on" }))
  };
}

function ContactSettingsEditor({ initial }: { initial?: ContactSettings }) {
  const [profile, setProfile] = useState<ContactSettings["profile"]>(initial?.profile ?? "automatic");
  const [fields, setFields] = useState(initial?.fields ?? contactPreset(initial?.profile && initial.profile !== "automatic" && initial.profile !== "custom" ? initial.profile : "hotel"));
  const [enabled, setEnabled] = useState(initial?.enabled !== false);
  const [recipient, setRecipient] = useState(initial?.recipient ?? (initial?.profile === "conference" ? "organizer" : initial?.profile === "business" || initial?.profile === "airport" ? "team" : "manager"));
  function preset(next: ContactSettings["profile"]) {
    setProfile(next);
    if (next !== "custom") setRecipient(next === "conference" ? "organizer" : next === "business" || next === "airport" ? "team" : "manager");
    if (next !== "automatic" && next !== "custom") setFields(contactPreset(next));
  }
  function changeField(key: ContactField, selected: boolean, required: boolean) {
    setProfile("custom");
    setFields(current => selected ? [...current.filter(field => field.key !== key), { key, required }] : current.filter(field => field.key !== key));
  }
  return <fieldset className="contact-settings-editor">
    <legend>Kontakt odgovorne osobe na kraju ankete</legend>
    <input name="contactRecipient" type="hidden" value={recipient} />
    <label className="check-control"><input name="contactEnabled" type="checkbox" checked={enabled} onChange={event => setEnabled(event.target.checked)} /> Ponudi kontakt menadžera, organizatora ili nadležnog tima</label>
    <select name="contactProfile" value={profile} onChange={event => preset(event.target.value as ContactSettings["profile"])} aria-label="Kontakt profil">
      <option value="automatic">Prema QR lokaciji</option>
      <option value="hotel">Hotel: ime i prezime, soba</option>
      <option value="restaurant">Restoran: ime i prezime, email, telefon</option>
      <option value="conference">Konferencija: kontakt organizatora</option>
      <option value="business">Firma: kontakt nadležnog tima</option>
      <option value="airport">Aerodrom: kontakt korisničke podrške</option>
      <option value="custom">Prilagođeni podaci</option>
    </select>
    {profile === "automatic" ? <p className="form-note">QR u sobi koristi hotelski profil. Ostale lokacije koriste kontakt putem emaila ili telefona. Oznaka stola ili ležaljke ostaje kontekst.</p> : <div className="contact-field-settings">
      {CONTACT_FIELDS.map(key => {
        const field = fields.find(item => item.key === key);
        return <div className="contact-field-setting" key={key}>
          <label className="check-control"><input name={`contactField:${key}`} type="checkbox" checked={Boolean(field)} disabled={!enabled} onChange={event => changeField(key, event.target.checked, field?.required ?? false)} /> {contactFieldLabels[key]}</label>
          <label className="check-control"><input name={`contactRequired:${key}`} type="checkbox" checked={field?.required ?? false} disabled={!enabled || !field} onChange={event => changeField(key, true, event.target.checked)} /> Obavezno ako gost želi kontakt</label>
        </div>;
      })}
    </div>}
    <p className="form-note">Ako osoba izabere Ne, anketa se šalje bez kontakt podataka. Za odgovor je potreban najmanje jedan način kontakta: soba, email ili telefon. Sala, sto, gate i zona ostaju kontekst QR koda.</p>
  </fieldset>;
}
